import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, Share, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const isStandalone = () => (
  window.matchMedia('(display-mode: standalone)').matches
  || window.navigator.standalone === true
);

const DISMISSED_KEY = 'zoea-pwa-install-dismissed';

const PwaInstallPrompt = () => {
  const location = useLocation();
  // The public setlist/song-reader share route is meant for anonymous,
  // one-off visitors (WhatsApp/QR links) reading lyrics full-screen on a
  // phone -- a fixed install banner would sit on top of the lyrics, so it's
  // suppressed there without touching PWA install behavior anywhere else.
  const isPublicShareRoute = location.pathname.startsWith('/share/worship/') || location.pathname.startsWith('/share/connectles/');
  const [installEvent, setInstallEvent] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [installed, setInstalled] = useState(isStandalone);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISSED_KEY) === 'true');
  const isiOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent)
    || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

  useEffect(() => {
    const handleInstallAvailable = (event) => {
      event.preventDefault();
      setInstallEvent(event);
    };
    const handleInstalled = () => {
      setInstallEvent(null);
      setShowIosHelp(false);
      setInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', handleInstallAvailable);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleInstallAvailable);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  if (isPublicShareRoute || installed || dismissed || (!installEvent && !isiOS)) return null;

  const handleInstall = async () => {
    if (isiOS && !installEvent) {
      setShowIosHelp(true);
      return;
    }
    if (!installEvent) return;
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome !== 'accepted') setInstallEvent(null);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  return (
    <div className="fixed bottom-4 right-4 z-[70] flex max-w-[calc(100vw-2rem)] flex-col items-end gap-2">
      {showIosHelp && (
        <div className="w-72 rounded-md border border-slate-200 bg-white p-4 shadow-xl" role="status">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-slate-900">Zoea installeren</p>
              <p className="mt-2 text-sm text-slate-600">
                Tik op delen <Share className="inline h-4 w-4" /> en kies ‘Zet op beginscherm’.
              </p>
            </div>
            <button type="button" aria-label="Installatie-instructie sluiten" onClick={() => setShowIosHelp(false)} className="text-slate-400 hover:text-slate-700">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center gap-1">
        <Button type="button" onClick={handleInstall} className="bg-teal-700 text-white shadow-lg hover:bg-teal-800">
          <Download className="mr-2 h-4 w-4" />
          Installeer Zoea
        </Button>
        <button
          type="button"
          aria-label="Installatiemelding sluiten"
          onClick={handleDismiss}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-400 shadow-lg hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default PwaInstallPrompt;
